import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Chip,
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  PowerSettingsNew as PowerIcon,
  AccountTree as AggregationIcon,
} from '@mui/icons-material';
import type { MongoAggregationDocumentData } from '@/services/graphql';
import { SftpMongoDestinationDialog, type MongoDestinationFormData } from './SftpMongoDestinationDialog';
import { SftpMongoColumnMapDialog, type MongoColumn } from './SftpMongoColumnMapDialog';
import { SftpMongoAggregationDialog, type AggregationFormData } from './SftpMongoAggregationDialog';

export interface MongoDestination {
  destinationId: string;
  connectionString?: string;
  databaseName: string;
  collectionName: string;
  isEnabled: boolean;
  mongoColumns?: MongoColumn[];
  aggregation?: MongoAggregationDocumentData | null;
}

interface SftpMongoDestinationsListProps {
  destinations: MongoDestination[];
  onAddDestination: (destination: MongoDestination) => void;
  onUpdateDestination: (destination: MongoDestination) => void;
  onRemoveDestination: (destinationId: string) => void;
  onToggleDestination: (destinationId: string, enable: boolean) => void;
  onUpdateMongoColumns: (destinationId: string, columns: MongoColumn[]) => void;
  onUpdateAggregation: (destinationId: string, aggregation: AggregationFormData) => void;
  isLoading?: boolean;
}

export const SftpMongoDestinationsList: React.FC<SftpMongoDestinationsListProps> = ({
  destinations,
  onAddDestination,
  onUpdateDestination,
  onRemoveDestination,
  onToggleDestination,
  onUpdateMongoColumns,
  onUpdateAggregation,
  isLoading = false,
}) => {
  const [destinationDialogOpen, setDestinationDialogOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<MongoDestinationFormData | undefined>();
  const [destinationMode, setDestinationMode] = useState<'create' | 'edit'>('create');
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [selectedDestForColumns, setSelectedDestForColumns] = useState<MongoDestination | undefined>();
  const [aggDialogOpen, setAggDialogOpen] = useState(false);
  const [selectedDestForAgg, setSelectedDestForAgg] = useState<MongoDestination | undefined>();
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [destToRemove, setDestToRemove] = useState<MongoDestination | undefined>();

  const handleAddDestination = () => {
    setSelectedDestination(undefined);
    setDestinationMode('create');
    setDestinationDialogOpen(true);
  };

  const handleEditDestination = (destination: MongoDestination) => {
    setSelectedDestination({
      destinationId: destination.destinationId,
      connectionString: '',
      databaseName: destination.databaseName,
      collectionName: destination.collectionName,
      isEnabled: destination.isEnabled,
    });
    setDestinationMode('edit');
    setDestinationDialogOpen(true);
  };

  const handleSaveDestination = (formData: MongoDestinationFormData) => {
    if (destinationMode === 'create') {
      onAddDestination(formData);
    } else {
      onUpdateDestination(formData);
    }
    setDestinationDialogOpen(false);
  };

  const handleEditColumns = (destination: MongoDestination) => {
    setSelectedDestForColumns(destination);
    setColumnDialogOpen(true);
  };

  const handleSaveColumns = (columns: MongoColumn[]) => {
    if (selectedDestForColumns) {
      onUpdateMongoColumns(selectedDestForColumns.destinationId, columns);
    }
    setColumnDialogOpen(false);
  };

  const handleEditAggregation = (destination: MongoDestination) => {
    setSelectedDestForAgg(destination);
    setAggDialogOpen(true);
  };

  const handleSaveAggregation = (data: AggregationFormData) => {
    if (selectedDestForAgg) {
      onUpdateAggregation(selectedDestForAgg.destinationId, data);
    }
    setAggDialogOpen(false);
  };

  const handleRemoveClick = (destination: MongoDestination) => {
    setDestToRemove(destination);
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = () => {
    if (destToRemove) {
      onRemoveDestination(destToRemove.destinationId);
    }
    setRemoveConfirmOpen(false);
    setDestToRemove(undefined);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">MongoDB Destinations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDestination}
          disabled={isLoading}
        >
          Add MongoDB Destination
        </Button>
      </Box>

      {destinations.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No MongoDB destinations configured. Click "Add MongoDB Destination" to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {destinations.map((destination) => (
            <Grid item xs={12} key={destination.destinationId}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {destination.databaseName}.{destination.collectionName}
                        </Typography>
                        <Chip
                          label={destination.isEnabled ? 'Enabled' : 'Disabled'}
                          color={destination.isEnabled ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip label="MongoDB" variant="outlined" size="small" />
                      </Box>
                      {destination.mongoColumns && destination.mongoColumns.length > 0 && (
                        <Typography variant="body2" color="textSecondary" mt={1}>
                          {destination.mongoColumns.length} column mapping(s) configured
                        </Typography>
                      )}
                      {destination.aggregation && (
                        <Typography variant="body2" color="textSecondary">
                          Aggregation: {destination.aggregation.targetCollection}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Configure Columns">
                        <IconButton onClick={() => handleEditColumns(destination)} disabled={isLoading}>
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Configure Aggregation">
                        <IconButton onClick={() => handleEditAggregation(destination)} disabled={isLoading}>
                          <AggregationIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={destination.isEnabled ? 'Disable' : 'Enable'}>
                        <IconButton
                          onClick={() => onToggleDestination(destination.destinationId, !destination.isEnabled)}
                          disabled={isLoading}
                          color={destination.isEnabled ? 'success' : 'default'}
                        >
                          <PowerIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEditDestination(destination)} disabled={isLoading}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={() => handleRemoveClick(destination)}
                          disabled={isLoading}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SftpMongoDestinationDialog
        open={destinationDialogOpen}
        onClose={() => setDestinationDialogOpen(false)}
        onSave={handleSaveDestination}
        destination={selectedDestination}
        mode={destinationMode}
      />

      {selectedDestForColumns && (
        <SftpMongoColumnMapDialog
          open={columnDialogOpen}
          onClose={() => setColumnDialogOpen(false)}
          onSave={handleSaveColumns}
          columns={selectedDestForColumns.mongoColumns || []}
          destinationName={`${selectedDestForColumns.databaseName}.${selectedDestForColumns.collectionName}`}
        />
      )}

      {selectedDestForAgg && (
        <SftpMongoAggregationDialog
          open={aggDialogOpen}
          onClose={() => setAggDialogOpen(false)}
          onSave={handleSaveAggregation}
          aggregation={selectedDestForAgg.aggregation}
          destinationName={`${selectedDestForAgg.databaseName}.${selectedDestForAgg.collectionName}`}
        />
      )}

      <Dialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
      >
        <DialogTitle>Remove MongoDB Destination</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the MongoDB destination "{destToRemove?.databaseName}.{destToRemove?.collectionName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
